import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ticketApi } from '../../api/ticket.api';
import { useAuth } from '../../store/AuthContext';
import { useWorkspaceNav } from '../../store/WorkspaceContext';
import { workspaceTabTitle } from '../../utils/workspaceConfig';
import { toPersian, timeAgo, parseBackendDateTime } from '../../utils/persianNumber';
import { Badge, Skeleton, EmptyState, PageHeader } from '../../components/ui/index';
import MissionsList from '../../components/missions/MissionsList';
import TicketFilterBar from '../../components/tickets/TicketFilterBar';
import CustomerTicketProgress from '../../components/tickets/CustomerTicketProgress';
import DashboardShortcuts from '../../components/dashboard/DashboardShortcuts';
import { useTicketFilters } from '../../hooks/useTicketFilters';
import { CUSTOMER_FILTERS, EMPLOYEE_DASHBOARD_FILTERS } from '../../utils/dashboardShortcuts';
import { getCustomerTicketStageBadge, isCustomerRole } from '../../utils/customerTicketStatus';
import { previewTicketWords } from '../../utils/ticketTitle';
import { User, Buildings, ArrowClockwise, CheckCircle, Tray } from '@phosphor-icons/react';

const POLL_MS = 10000;

// Customer roles — 3 high-signal filters (see dashboardShortcuts.js).
const FILTERS = CUSTOMER_FILTERS;

// Exactly 3 states for an employee's tickets, using refrenced_seen (this
// employee's own "seen" flag - see queryEmployeeSeenTicket) combined with
// responsed (customer-facing answered status, only true once a manager
// confirms - see queryResponseConfirmation):
//   1. دیده‌نشده          - refrenced_seen=0 (not opened yet)
//   2. دیده‌شده، بدون پاسخ - refrenced_seen=1 AND responsed=0 (opened, no
//                           confirmed answer yet - includes an employee's
//                           own unconfirmed draft response)
//   3. پاسخ داده‌شده       - responsed=1
const EMPLOYEE_FILTERS = EMPLOYEE_DASHBOARD_FILTERS;

const ENDPOINTS = {
  simple_user:  { all: 'getSimpleUserAll', active: 'getSimpleUserActive', unseen: 'getUserUnseen', seen: 'getUserSeen', noresp: 'getUserUnresponsed' },
  manager_user: { all: 'getManagerUserAll', active: 'getManagerUserActive', unseen: 'getManagerUserUnseen', seen: 'getManagerUserSeen', noresp: 'getManagerUserUnresponsed' },
};

// manager_employee/simple_employee are NOT the same role as manager_user/
// simple_user on the backend — get_manager_user_all_message etc. are gated
// to checkLogin(['manager_user']) only (views.py), so an employee session
// always 403s there. Employees have their own list endpoint instead:
// get_ticket_list_view, which explicitly supports manager_employee/
// simple_employee (QueryFunctions.queryGetTicketListView).
const EMPLOYEE_FILTER_PARAMS = {
  unseen:          { refrenced_seen: 0 },
  seen_unanswered: { refrenced_seen: 1, responsed: 0 },
  answered:        { responsed: 1, closed: 0 },
  closed:          { closed: 1 },
  all:             {},
};
const isEmployeeRole = (type) => type === 'manager_employee' || type === 'simple_employee';

// ticket_status_view_combined.ticket_status (see ai_chat_and_views.sql) is
// a NUMERIC code, not the string keys this used to be indexed by
// ('answered'/'closed'/...) - STATUS_META[t.status] never matched
// anything, so every card silently fell back to STATUS_META.new
// regardless of its real status. Also doubles as the open/closed
// indicator: 3 means the customer closed the ticket (ticket_is_active=0),
// takes priority over responsed.
const STATUS_META = {
  0: { label: 'جدید',              color: '#F4640B', bg: '#F5F7FA' },
  1: { label: 'در انتظار',          color: '#F4640B', bg: '#F5F7FA' },
  2: { label: 'پاسخ داده شده (باز)', color: '#71A95B', bg: '#F5F7FA' },
  3: { label: 'بسته شده',           color: '#17202A', bg: '#F5F7FA' },
};
const DEFAULT_STATUS_META = { label: 'نامشخص', color: '#17202A', bg: '#F5F7FA' };

export default function UserDashboard() {
  const navigate = useNavigate();
  const workspaceNav = useWorkspaceNav();
  const { user } = useAuth();
  const [filter, setFilter] = useState('all');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [counts, setCounts] = useState({});
  const pollRef = useRef(null);

  const userType = user?.type || 'simple_user';
  const endpointMap = ENDPOINTS[userType] || ENDPOINTS.simple_user;
  const filterList = isEmployeeRole(userType) ? EMPLOYEE_FILTERS : FILTERS;

  // simple_employee's dashboard splits into two sections - their own
  // tickets, and any mission they've been personally dispatched on (see
  // get_my_missions) - previously missions were only reachable via a
  // separate sidebar page, disconnected from the main dashboard.
  const showMissionsSection = userType === 'simple_employee';
  const [section, setSection] = useState('tickets');

  const handleShortcutAction = useCallback((action) => {
    if (action.startsWith('filter:')) {
      setSection('tickets');
      setFilter(action.slice('filter:'.length));
      return;
    }
    if (action.startsWith('section:')) {
      setSection(action.slice('section:'.length));
    }
  }, []);

  // Employees land on 'unseen' by default instead of 'all' - only fires
  // once userType actually resolves to an employee role (the 'simple_user'
  // fallback above never matches isEmployeeRole, so this can't fire
  // prematurely before the user loads).
  useEffect(() => {
    if (isEmployeeRole(userType)) setFilter('unseen');
  }, [userType]);

  const fetchTickets = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = isEmployeeRole(userType)
        ? await ticketApi.getTicketListView(EMPLOYEE_FILTER_PARAMS[filter] || EMPLOYEE_FILTER_PARAMS.unseen)
        : await ticketApi[endpointMap[filter] || endpointMap.all]();
      const data = Array.isArray(res.data) ? res.data : res.data?.tickets || [];
      setTickets(data);
    } catch {}
    setLoading(false);
  }, [filter, endpointMap, userType]);

  // Per-tab message counters (employees only): the list endpoint has no
  // count-only mode, so each filter is fetched once and only its length is
  // kept. A failed request leaves that tab's previous count untouched.
  const fetchCounts = useCallback(async () => {
    if (!isEmployeeRole(userType)) return;
    const ids = Object.keys(EMPLOYEE_FILTER_PARAMS);
    const results = await Promise.all(ids.map(async (id) => {
      try {
        const res = await ticketApi.getTicketListView(EMPLOYEE_FILTER_PARAMS[id]);
        const data = Array.isArray(res.data) ? res.data : res.data?.tickets || [];
        return [id, data.length];
      } catch {
        return [id, undefined];
      }
    }));
    setCounts((prev) => {
      const next = { ...prev };
      results.forEach(([id, n]) => { if (n !== undefined) next[id] = n; });
      return next;
    });
  }, [userType]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);
  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  useEffect(() => {
    pollRef.current = setInterval(() => { fetchTickets(true); fetchCounts(); }, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [fetchTickets, fetchCounts]);

  // Employee lists (get_ticket_list_view) carry a `company` field since a
  // dispatcher/employee sees tickets from many different customer
  // companies; customer lists don't (a customer only ever sees their own
  // company's tickets), so the company combo box only makes sense - and
  // only renders, see showCompanyFilter below - for employee roles.
  const { filteredSorted, company, setCompany, sortBy, setSortBy, companyOptions } =
    useTicketFilters(tickets, { companyKey: 'company', dateKey: 'send_date' });

  const filtered = filteredSorted.filter((t) => {
    if (!search) return true;
    return (t.ticket_title || t.title || '').includes(search) || (t.ticket_text || '').includes(search);
  });

  return (
    <div className="fade-in">
      <PageHeader
        title={isEmployeeRole(userType) ? 'تیکت‌ها' : 'تیکت‌های من'}
        subtitle={section === 'missions'
          ? 'ماموریت‌های محول‌شده'
          : `${toPersian(tickets.length)} تیکت در این بخش`}
      />

      <DashboardShortcuts
        role={userType}
        onAction={handleShortcutAction}
        activeSection={showMissionsSection ? section : undefined}
        activeFilter={!isEmployeeRole(userType) ? filter : undefined}
      />

      {showMissionsSection && section === 'missions' ? (
        <MissionsList />
      ) : (
        <>
          {/* Filter pills */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            {filterList.map((f) => (
              <button key={f.id} onClick={() => setFilter(f.id)} style={{
                padding: '7px 16px', borderRadius: 20, border: `1.5px solid ${filter === f.id ? f.color : '#e2e8f0'}`,
                background: filter === f.id ? f.color + '12' : '#fff',
                color: filter === f.id ? f.color : '#64748b',
                fontWeight: filter === f.id ? 700 : 500, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
              }}>
                {f.label}
                {isEmployeeRole(userType) && counts[f.id] !== undefined && (
                  <span style={{
                    marginRight: 6, minWidth: 20, padding: '1px 6px', borderRadius: 10, display: 'inline-block',
                    background: filter === f.id ? f.color : '#f1f5f9',
                    color: filter === f.id ? '#fff' : '#64748b',
                    fontSize: 11, fontWeight: 700, lineHeight: '16px', textAlign: 'center',
                  }}>
                    {toPersian(counts[f.id])}
                  </span>
                )}
              </button>
            ))}
            <TicketFilterBar
              sortBy={sortBy} setSortBy={setSortBy}
              company={company} setCompany={setCompany}
              companyOptions={companyOptions}
              showCompanyFilter={isEmployeeRole(userType)}
            />
            <div style={{ marginRight: 'auto' }}>
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="جستجو در تیکت‌ها..." style={{ padding: '7px 14px', border: '1.5px solid #e2e8f0', borderRadius: 20, fontSize: 12, outline: 'none', fontFamily: 'inherit', width: 200 }} />
            </div>
            <button onClick={() => fetchTickets()} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 20, border: '1.5px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
              <ArrowClockwise size={13} weight="regular" /> بروزرسانی
            </button>
          </div>

          {/* Cards */}
          {loading
            ? <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[1,2,3,4].map((k) => <TicketSkeleton key={k} />)}
              </div>
            : filtered.length === 0
              ? <EmptyState icon={<Tray size={44} weight="regular" />} title="تیکتی یافت نشد" subtitle="با تغییر فیلتر یا کلمه جستجو دوباره امتحان کنید" />
              : <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {filtered.map((t) => (
                    <TicketCard key={t.id || t.ticket_id} ticket={t} filter={filter} showSender={isEmployeeRole(userType)} isCustomer={isCustomerRole(userType)} onClick={() => {
                      const ticketId = t.id || t.ticket_id;
                      // Employees: open the ticket as its own workspace tab
                      // (falls back to a plain navigate outside the
                      // workspace, e.g. for dispatcher). Customer roles keep
                      // their normal full-page ticket detail navigation.
                      if (isEmployeeRole(userType)) {
                        workspaceNav(`/tickets/${ticketId}/process`, t.ticket_title || t.title || workspaceTabTitle(`/tickets/${ticketId}/process`));
                      } else {
                        navigate(`/tickets/${ticketId}`);
                      }
                    }} />
                  ))}
                </div>
          }
        </>
      )}
    </div>
  );
}

// get_ticket_list_view (queryGetTicketListView) - what employee dashboards
// fetch from - never returns a numeric `status` code like
// ticket_status_view_combined does, only a `responsed` boolean. Falling
// through to STATUS_META[t.status] here always misses (t.status is
// undefined) and every employee ticket permanently showed the
// DEFAULT_STATUS_META "نامشخص" badge regardless of its real state.
const EMPLOYEE_STATUS_META = {
  answered:   { label: 'پاسخ داده شده', color: '#71A95B', bg: '#F5F7FA' },
  unanswered: { label: 'در انتظار پاسخ', color: '#F4640B', bg: '#F5F7FA' },
};

// Waiting-time tint for still-unanswered tickets: starts at 2 days
// (pale yellow) and ramps to 30+ days (red), so an old unanswered message
// visibly stands out from a fresh one in the list. Answered tickets never
// get this tint - the wait is over.
const AGING_MIN_DAYS = 2;
const AGING_MAX_DAYS = 30;
const AGING_FROM = { bg: [254, 249, 195], border: [250, 204, 21] };  // pale yellow
const AGING_TO   = { bg: [254, 226, 226], border: [239, 68, 68] };   // pale red
const mix = (a, b, t) => Math.round(a + (b - a) * t);
const rgb = (c) => `rgb(${c[0]},${c[1]},${c[2]})`;

function agingTint(sendDate, responsed) {
  if (responsed || !sendDate) return null;
  const sent = parseBackendDateTime(sendDate);
  if (isNaN(sent)) return null;
  const days = (Date.now() - sent.getTime()) / 86400000;
  if (days < AGING_MIN_DAYS) return null;
  const t = Math.min(1, (days - AGING_MIN_DAYS) / (AGING_MAX_DAYS - AGING_MIN_DAYS));
  return {
    bg: rgb(AGING_FROM.bg.map((c, i) => mix(c, AGING_TO.bg[i], t))),
    border: rgb(AGING_FROM.border.map((c, i) => mix(c, AGING_TO.border[i], t))),
  };
}

function TicketCard({ ticket: t, filter, onClick, showSender = false, isCustomer = false }) {
  const status = t.status !== undefined
    ? (STATUS_META[t.status] ?? DEFAULT_STATUS_META)
    : (t.responsed ? EMPLOYEE_STATUS_META.answered : EMPLOYEE_STATUS_META.unanswered);
  const customerBadge = isCustomer ? getCustomerTicketStageBadge(t) : null;
  const filterMeta = [...FILTERS, ...EMPLOYEE_FILTERS].find((f) => f.id === filter);
  const accentColor = filterMeta?.color || (customerBadge?.color ?? status.color);
  const tint = agingTint(t.send_date, t.responsed);
  const baseBorderColor = tint?.border || '#e2e8f0';

  return (
    <div onClick={onClick} className="fade-in" style={{ background: tint?.bg || '#fff', borderWidth: 1, borderStyle: 'solid', borderColor: baseBorderColor, borderRadius: 12, padding: '16px 20px', cursor: 'pointer', transition: 'all 0.15s', position: 'relative', overflow: 'hidden', display: 'flex', gap: 16, alignItems: 'flex-start' }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = accentColor + '60'; e.currentTarget.style.boxShadow = `0 4px 16px ${accentColor}18`; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = baseBorderColor; e.currentTarget.style.boxShadow = 'none'; }}
    >
      {/* Accent bar */}
      <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 3, background: accentColor, borderRadius: '0 12px 12px 0' }} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '70%' }}>
            {t.ticket_title || t.title || 'بدون عنوان'}
          </h3>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexDirection: 'column', alignItems: 'flex-end' }}>
            {isCustomer
              ? <span style={{ fontSize: 10, fontWeight: 600, color: customerBadge.color, background: customerBadge.bg, padding: '2px 8px', borderRadius: 10 }}>{customerBadge.label}</span>
              : <span style={{ fontSize: 10, fontWeight: 600, color: status.color, background: status.bg, padding: '2px 8px', borderRadius: 10 }}>{status.label}</span>}
          </div>
        </div>
        {isCustomer && (
          <div style={{ marginBottom: 8 }}>
            <CustomerTicketProgress ticket={t} compact />
          </div>
        )}
        {t.ticket_text && (
          <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 10px', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: isCustomer ? 2 : 3, WebkitBoxOrient: 'vertical', lineHeight: 1.6 }}>
            {isCustomer ? t.ticket_text : (previewTicketWords(t.ticket_text, 40) || t.ticket_text)}
          </p>
        )}
        {/* response_title is optional (see TicketProcessPage's reply form)
            and often left blank - checking only response_title hid the
            entire answer indicator for any response sent without a title,
            even though description (the actual answer text) was there. */}
        {(t.response_title || t.description) && (
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '8px 12px', marginBottom: 8 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: '#15803d', display: 'inline-flex', alignItems: 'center', gap: 3 }}><CheckCircle size={11} weight="regular" /> پاسخ: </span>
            <span style={{ fontSize: 12, color: '#334155' }}>{t.description || t.response_title}</span>
          </div>
        )}
        {/* Sender identity - who sent this and from which company - only
            meaningful to employees/managers handling tickets from many
            different customers; a customer's own dashboard only ever shows
            their own tickets, so this stays hidden there. company is null
            exactly for guest_user senders (see queryGetTicketListView). */}
        {showSender && (t.sender_first_name || t.sender_last_name || t.company || t.is_guest) && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
            {(t.sender_first_name || t.sender_last_name) && (
              <span style={{ fontSize: 11, color: '#475569', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <User size={12} weight="regular" /> {[t.sender_first_name, t.sender_last_name].filter(Boolean).join(' ')}
                {t.sender_username && <span style={{ color: '#94a3b8' }}> ({t.sender_username})</span>}
              </span>
            )}
            {t.company
              ? <span style={{ fontSize: 11, color: '#475569', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Buildings size={12} weight="regular" /> {t.company_display || t.company}</span>
              : <Badge variant="amber"><User size={11} weight="regular" /> مهمان</Badge>}
          </div>
        )}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 4 }}>
          {t.send_date && <span style={{ fontSize: 11, color: '#94a3b8' }}>{timeAgo(t.send_date)}</span>}
          {t.self_ticket && <Badge variant="blue">تیکت من</Badge>}
        </div>
      </div>
    </div>
  );
}

function TicketSkeleton() {
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Skeleton h={18} w="60%" />
      <Skeleton h={14} w="90%" />
      <Skeleton h={14} w="75%" />
    </div>
  );
}
